import classNames from 'classnames'
import React, { useState } from 'react'
import {
  AD_NETWORKS,
  AFFILIATE_POLICY_URL,
  MARKETER_SOCIAL_PLATFORMS,
  PROHIBITED_ACTIVITIES,
  PROHIBITED_ACTIVITY_IDS,
  TRAFFIC_SOURCES,
} from './content'
import { styles } from './styles'

interface FlowProps {
  submitting: boolean
  error: string
  onBack: () => void
  onSubmit: (
    answers: Record<string, any>,
    consents: Record<string, boolean>
  ) => void
}

const MarketerFlow = ({ submitting, error, onBack, onSubmit }: FlowProps) => {
  const classes = styles()
  const [step, setStep] = useState(1)
  const [openCat, setOpenCat] = useState<string | null>(null)
  const [acks, setAcks] = useState<Record<string, boolean>>({})
  const [trafficSource, setTrafficSource] = useState<
    'soc' | 'net' | 'both' | null
  >(null)
  const [socialAccounts, setSocialAccounts] = useState<Record<string, string>>(
    {}
  )
  const [selectedNetworks, setSelectedNetworks] = useState<
    Record<string, boolean>
  >({})
  const [networkFields, setNetworkFields] = useState<string[]>([''])
  const [consents, setConsents] = useState<Record<string, boolean>>({})
  const [localError, setLocalError] = useState('')

  const showSocial = trafficSource === 'soc' || trafficSource === 'both'
  const showNet = trafficSource === 'net' || trafficSource === 'both'

  const goTo = (n: number) => {
    setLocalError('')
    setStep(n)
  }

  const setNetworkField = (idx: number, value: string) =>
    setNetworkFields(prev => prev.map((v, i) => (i === idx ? value : v)))
  const addNetworkField = () => setNetworkFields(prev => [...prev, ''])
  const removeNetworkField = (idx: number) =>
    setNetworkFields(prev =>
      prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
    )

  const next = () => {
    setLocalError('')
    if (step === 1 && !PROHIBITED_ACTIVITY_IDS.every(id => acks[id])) {
      setLocalError(
        'Please expand each category and confirm you will not engage in it.'
      )
      return
    }
    if (step === 2 && !trafficSource) {
      setLocalError('Please select where you are buying traffic.')
      return
    }
    if (step === 3) {
      if (
        showSocial &&
        !MARKETER_SOCIAL_PLATFORMS.some(p =>
          (socialAccounts[p.id] || '').trim()
        )
      ) {
        setLocalError('Please enter at least one social ad account.')
        return
      }
      const hasSelectedNetwork = Object.keys(selectedNetworks).some(
        k => selectedNetworks[k]
      )
      const hasOtherNetwork = networkFields.some(v => v.trim())
      if (showNet && !hasSelectedNetwork && !hasOtherNetwork) {
        setLocalError(
          'Please select or name at least one ad network or traffic platform.'
        )
        return
      }
    }
    setStep(step + 1)
  }

  const generate = () => {
    if (!consents.terms || !consents.consequences) {
      setLocalError('Please check both boxes to continue.')
      return
    }
    setLocalError('')
    const cleanedAccounts: Record<string, string> = {}
    MARKETER_SOCIAL_PLATFORMS.forEach(p => {
      const v = (socialAccounts[p.id] || '').trim()
      if (v) cleanedAccounts[p.id] = v
    })
    onSubmit(
      {
        prohibitionsAck: PROHIBITED_ACTIVITY_IDS.reduce(
          (acc, id) => ({ ...acc, [id]: true }),
          {} as Record<string, boolean>
        ),
        trafficSource,
        socialAccounts: showSocial ? cleanedAccounts : {},
        adNetworks: [
          ...(showNet ? AD_NETWORKS.filter(n => selectedNetworks[n]) : []),
          ...networkFields.map(v => v.trim()).filter(Boolean),
        ],
      },
      { terms: !!consents.terms, consequences: !!consents.consequences }
    )
  }

  return (
    <div>
      <div className={classes.pips} style={{ marginBottom: 16 }}>
        {[1, 2, 3, 4].map(n => (
          <span
            key={n}
            className={classNames(classes.pip, { [classes.pipOn]: n <= step })}
          />
        ))}
      </div>

      {step === 1 && (
        <div>
          <p className={classes.intro}>
            Confirm that you will not engage in any of the following. Expand
            each category to read and confirm.
          </p>
          {PROHIBITED_ACTIVITIES.map(cat => {
            const isOpen = openCat === cat.id
            return (
              <div key={cat.id}>
                <div
                  className={classNames(classes.catHeader, {
                    [classes.catHeaderAgreed]: acks[cat.id],
                  })}
                  onClick={() => setOpenCat(isOpen ? null : cat.id)}
                >
                  <span style={{ fontSize: 13 }}>{cat.title}</span>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    {isOpen ? '−' : '+'}
                  </span>
                </div>
                {isOpen && (
                  <div className={classes.catBody}>
                    <span className={classes.prohibitedTag}>
                      The following activity is prohibited
                    </span>
                    {cat.details.map((d, i) => (
                      <p key={i} style={{ margin: '0 0 8px' }}>
                        {d}
                      </p>
                    ))}
                    <label
                      className={classes.consentRow}
                      style={{ marginBottom: 0 }}
                    >
                      <input
                        type="checkbox"
                        checked={!!acks[cat.id]}
                        onChange={e =>
                          setAcks(prev => ({
                            ...prev,
                            [cat.id]: e.target.checked,
                          }))
                        }
                      />
                      <span>
                        I confirm I will not engage in the activity described
                        above.
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )
          })}
          {(localError || error) && (
            <p className={classes.error}>{localError || error}</p>
          )}
          <div className={classes.btnRow}>
            <button type="button" className={classes.backBtn} onClick={onBack}>
              Back
            </button>
            <button
              type="button"
              className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
              onClick={next}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <p className={classes.intro}>
            Where will you be buying traffic? Be specific. This is part of your
            compliance record.
          </p>
          {TRAFFIC_SOURCES.map(src => (
            <button
              key={src.id}
              type="button"
              className={classNames(classes.card, {
                [classes.cardOn]: trafficSource === src.id,
              })}
              onClick={() => setTrafficSource(src.id)}
            >
              <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 3px' }}>
                {src.title}
              </p>
              <p style={{ fontSize: 11, color: '#888', margin: 0 }}>
                {src.description}
              </p>
            </button>
          ))}
          {(localError || error) && (
            <p className={classes.error}>{localError || error}</p>
          )}
          <div className={classes.btnRow}>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => goTo(1)}
            >
              Back
            </button>
            <button
              type="button"
              className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
              onClick={next}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          {showSocial && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label className={classes.label}>Your social ad accounts</label>
              <p className={classes.hint}>
                Enter your account name for each platform you use. At least one
                required.
              </p>
              {MARKETER_SOCIAL_PLATFORMS.map(p => (
                <div key={p.id} className={classes.socialRow}>
                  <span
                    style={{ fontSize: 12, fontWeight: 500, minWidth: 110 }}
                  >
                    {p.label}
                  </span>
                  <input
                    className={classes.input}
                    style={{
                      height: 32,
                      border: 'none',
                      background: 'transparent',
                    }}
                    placeholder="@handle or ID"
                    value={socialAccounts[p.id] || ''}
                    onChange={e =>
                      setSocialAccounts(prev => ({
                        ...prev,
                        [p.id]: e.target.value,
                      }))
                    }
                  />
                </div>
              ))}
            </div>
          )}
          {showNet && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label className={classes.label}>
                Ad networks / traffic platforms
              </label>
              <p className={classes.hint}>Select all the platforms you use.</p>
              <div className={classes.tileGrid}>
                {AD_NETWORKS.map(net => (
                  <div
                    key={net}
                    className={classNames(classes.tile, {
                      [classes.tileOn]: selectedNetworks[net],
                    })}
                    onClick={() =>
                      setSelectedNetworks(prev => ({
                        ...prev,
                        [net]: !prev[net],
                      }))
                    }
                  >
                    {net}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className={classes.label}>Other platforms</label>
            <p className={classes.hint}>
              Name any other platform not listed above. Add a field for each
              one.
            </p>
            {networkFields.map((value, idx) => (
              <div key={idx} className={classes.socialRow}>
                <input
                  className={classes.input}
                  style={{
                    height: 32,
                    border: 'none',
                    background: 'transparent',
                  }}
                  placeholder="Platform name"
                  value={value}
                  onChange={e => setNetworkField(idx, e.target.value)}
                />
                {networkFields.length > 1 && (
                  <button
                    type="button"
                    className={classes.removeFieldBtn}
                    onClick={() => removeNetworkField(idx)}
                    aria-label="Remove network"
                  >
                    {'×'}
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className={classes.addBtn}
              onClick={addNetworkField}
            >
              + Add another platform
            </button>
          </div>
          {(localError || error) && (
            <p className={classes.error}>{localError || error}</p>
          )}
          <div className={classes.btnRow}>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => goTo(2)}
            >
              Back
            </button>
            <button
              type="button"
              className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
              onClick={next}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <p className={classes.intro}>
            Confirm you've read our affiliate policy before generating your
            link.
          </p>
          <div className={classes.summary}>
            <p style={{ margin: '0 0 6px' }}>
              By generating this link you agree to Chirpyest's affiliate terms.
            </p>
            <a
              href={AFFILIATE_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12 }}
            >
              Read the full affiliate policy
            </a>
          </div>
          <label className={classes.consentRow}>
            <input
              type="checkbox"
              checked={!!consents.terms}
              onChange={e =>
                setConsents(prev => ({ ...prev, terms: e.target.checked }))
              }
            />
            <span>
              I have read the Chirpyest affiliate policy and agree to all terms,
              including the prohibited activities listed above.
            </span>
          </label>
          <label className={classes.consentRow}>
            <input
              type="checkbox"
              checked={!!consents.consequences}
              onChange={e =>
                setConsents(prev => ({
                  ...prev,
                  consequences: e.target.checked,
                }))
              }
            />
            <span>
              I understand that violations may result in my share links being
              paused or my account being removed.
            </span>
          </label>
          {(localError || error) && (
            <p className={classes.error}>{localError || error}</p>
          )}
          <div className={classes.btnRow}>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => goTo(3)}
            >
              Back
            </button>
            <button
              type="button"
              className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
              disabled={submitting}
              onClick={generate}
            >
              {submitting ? 'Generating' : 'Generate share link'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketerFlow
